import type { HarvestLog } from '../../types/production'

import { getOfflineDb } from '../dbFactory'

export type HarvestLogListParams = {
  farmId?: string
  productionUnitId?: string
  lot?: string
  lotId?: string
  productionRowId?: string
  fromRecordedAt?: string
  toRecordedAt?: string
}

export class DexieHarvestLogRepo {
  async list(params: HarvestLogListParams = {}): Promise<HarvestLog[]> {
    const farmId = params.farmId?.trim() || undefined
    const productionUnitId = params.productionUnitId?.trim() || undefined
    const lot = params.lot?.trim() || undefined
    const lotId = params.lotId?.trim() || undefined
    const productionRowId = params.productionRowId?.trim() || undefined
    const fromRecordedAt = params.fromRecordedAt?.trim() || undefined
    const toRecordedAt = params.toRecordedAt?.trim() || undefined

    const db = getOfflineDb()
    const table = db.harvestLogs

    let base: HarvestLog[]

    if (farmId && productionUnitId) {
      base = await table.where('[farmId+productionUnitId]').equals([farmId, productionUnitId]).toArray()
    } else if (farmId) {
      base = await table.where('farmId').equals(farmId).toArray()
    } else if (productionUnitId) {
      base = await table.where('productionUnitId').equals(productionUnitId).toArray()
    } else {
      base = await table.toArray()
    }

    const fromTime = fromRecordedAt ? new Date(fromRecordedAt).getTime() : undefined
    const toTime = toRecordedAt ? new Date(toRecordedAt).getTime() : undefined

    return base
      .filter((log) => {
        if (lot && (log.lot ?? '') !== lot) return false
        if (lotId && (log.lotId ?? '') !== lotId) return false
        if (productionRowId && (log.productionRowId ?? '') !== productionRowId) return false

        if (fromTime !== undefined || toTime !== undefined) {
          const time = new Date(log.recordedAt).getTime()
          if (Number.isNaN(time)) return false
          if (fromTime !== undefined && time < fromTime) return false
          if (toTime !== undefined && time > toTime) return false
        }

        return true
      })
      .sort((left, right) => right.recordedAt.localeCompare(left.recordedAt))
  }

  async getById(id: string): Promise<HarvestLog | undefined> {
    return getOfflineDb().harvestLogs.get(id)
  }

  async upsert(log: HarvestLog): Promise<void> {
    await getOfflineDb().harvestLogs.put(log)
  }

  async upsertMany(logs: HarvestLog[]): Promise<void> {
    if (logs.length === 0) return
    await getOfflineDb().harvestLogs.bulkPut(logs)
  }

  async delete(id: string): Promise<void> {
    await getOfflineDb().harvestLogs.delete(id)
  }

  async deleteMany(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await getOfflineDb().harvestLogs.bulkDelete(ids)
  }

  async clear(): Promise<void> {
    await getOfflineDb().harvestLogs.clear()
  }
}

export const dexieHarvestLogRepo = new DexieHarvestLogRepo()
